// Must run before any module touches Amplify Auth (e.g. the store's auth init).
import './plugins/amplify'

import { createApp } from 'vue'
import { createHead } from '@unhead/vue'
import { createApolloProvider } from '@vue/apollo-option'

import App from './app.vue'
import router from '@router'
import store from '@state/store'
import apolloClient, { cacheRestored } from './graphql/apollo-client'
import * as outbox from './graphql/outbox'
import * as uploadQueue from './graphql/upload-queue'
import './registerServiceWorker'
import { registerInstallPromptSuppression } from '@utils/install-prompt'
import vuetify from './plugins/vuetify'
import { installTheme } from './plugins/theme'
import i18n from './plugins/i18n'
import { reportError } from '@utils/report-error'
import { initSentry } from './plugins/sentry'

registerInstallPromptSuppression()

const head = createHead()
// Global title template, replacing the old vue-meta `titleTemplate` in app.vue.
// Components set `metaInfo: { title: 'Foo' }` and get "Foo | BowVue".
head.push({
  titleTemplate: (title) => (title ? `${title} | BowVue` : 'BowVue'),
})

// Options-API meta: read each component's `metaInfo` option and feed it to
// unhead. Replaces vue-meta. Components keep their existing `metaInfo` blocks.
function applyMeta() {
  const mi = this.$options.metaInfo
  if (!mi) return
  const resolved = typeof mi === 'function' ? mi.call(this) : mi
  if (this.__headEntry) {
    this.__headEntry.patch(resolved)
  } else {
    this.__headEntry = head.push(resolved)
  }
}
const metaInfoMixin = {
  created() {
    applyMeta.call(this)
  },
  updated() {
    // Re-evaluate dynamic (function) metaInfo as reactive data changes.
    if (typeof this.$options.metaInfo === 'function') applyMeta.call(this)
  },
  beforeUnmount() {
    if (this.__headEntry) this.__headEntry.dispose()
  },
}

// Expose a single reactive mobile flag to every component so data tables can
// bind `:mobile="isMobileDevice"` (Vuetify 3's stacked mobile layout). Uses
// Vuetify's viewport-reactive display so it works for both real devices and
// responsive testing (smAndDown = < 960px; the default mobileBreakpoint of
// `lg`/1280 would stack tables on laptops too).
const mobileMixin = {
  computed: {
    isMobileDevice() {
      return this.$vuetify?.display?.smAndDown ?? false
    },
  },
}

const apolloProvider = createApolloProvider({
  defaultClient: apolloClient,
  defaultOptions: {
    $query: {
      fetchPolicy: 'cache-and-network',
    },
  },
})

const app = createApp(App)

// Surface otherwise-uncaught component errors and unhandled promise rejections
// through the central reporter (previously these were lost).
app.config.errorHandler = (err, instance, info) => {
  reportError(err, { source: 'vue', info })
}
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' })
  })
}

// Frontend error monitoring + tracing. Init after the errorHandler above so
// @sentry/vue's Vue integration chains to reportError instead of replacing it.
initSentry(app, router)

// A production deploy publishes new content-hashed chunks and deletes the old
// ones. A browser still running a previous build (e.g. a stale, long-cached
// index.html) fails to fetch a now-missing chunk when navigating to a
// lazy-loaded route, throwing "error loading dynamically imported module".
// Recover by reloading once onto the current build. The sessionStorage guard
// prevents a reload loop if a chunk is genuinely broken rather than just stale.
function reloadForStaleChunk() {
  const KEY = 'bv:stale-chunk-reload-at'
  const last = Number(sessionStorage.getItem(KEY) || 0)
  if (Date.now() - last < 10000) return
  sessionStorage.setItem(KEY, String(Date.now()))
  window.location.reload()
}

function isDynamicImportError(err) {
  const msg = String(err?.message || err || '')
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('Importing a module script failed')
  )
}

if (typeof window !== 'undefined') {
  // Vite fires this when its preload helper can't fetch a dynamically imported chunk.
  window.addEventListener('vite:preloadError', (event) => {
    reportError(event?.payload || new Error('vite:preloadError'), {
      source: 'vite:preloadError',
    })
    reloadForStaleChunk()
  })
}

// Lazy route-component imports that reject surface through the router's error
// hook; treat a dynamic-import failure as a stale build and reload.
router.onError((error) => {
  if (isDynamicImportError(error)) {
    reportError(error, { source: 'router:dynamic-import' })
    reloadForStaleChunk()
  }
})

app.mixin(metaInfoMixin)
app.mixin(mobileMixin)
app.use(store)
app.use(router)
app.use(i18n)
app.use(vuetify)
// After app.use(vuetify) so the theme instance exists: applies the stored
// choice (or the OS setting, when that choice is 'system') and starts
// following the OS while it stays on 'system'.
installTheme(vuetify)
app.use(head)
app.use(apolloProvider)

// Seed the offline queues' FIFO counters from anything persisted across
// restarts (Infrared Offline change-log + photo uploads). Fire-and-forget:
// nothing enqueues before a user interaction, and both fail safe to seq 0.
outbox.initSeq().catch(() => {})
uploadQueue.initSeq().catch(() => {})

// Wait for the persisted Apollo cache (IndexedDB) to hydrate before mounting so
// offline reads are available on first paint and queries don't run against an
// empty cache. Guard with a timeout so a slow or unavailable IndexedDB can
// never block startup. Worst case we mount with a cold cache and hydrate a
// moment later (Apollo updates reactively).
const CACHE_HYDRATION_TIMEOUT_MS = 3000
Promise.race([
  cacheRestored,
  new Promise((resolve) => setTimeout(resolve, CACHE_HYDRATION_TIMEOUT_MS)),
]).finally(() => {
  app.mount('#app')
})
